import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Wallet, Save } from 'lucide-react';
import { Card } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Checkbox, TextField } from '@components/base/inputs';
import { useToast } from '@components/base/feedback';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  SelectFieldOptions,
  RadioFieldOptions,
  TextFieldOptions,
  UseEntityFormReturn,
} from '@components/integrated/entity-form';
import { useRemoteComponentConfig } from '@components/integrated/shared';
import {
  DataList,
  useDataList,
  dataListWorkflowRegistry,
} from '@components/integrated/data-list';
import type { DataListConfig } from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';
import type {
  ProvisionToSettleRow,
  SinperRow,
  RibRow,
  ModregRow,
  MonnaieRow,
  ParamReglementRow,
  CdopRow,
} from '../types';
import { useClaimDataStore } from './claimDataStore';

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

type ErpListResponse<T> = ErpProxyResponse<{ total: number; data: T[] }>;

export interface SettlementCardConfig {
  defaultModeReglement?: string;
  defaultBanque?: string;
  defaultMonnaie?: string;
  defaultNature?: string;
}

interface SettlementCardProps {
  ses: string;
  idSin: string;
  defaultModeReglement?: string;
  defaultBanque?: string;
  defaultMonnaie?: string;
  defaultNature?: string;
  /**
   * Tenant-tunable allow-list of `CD_OP` codes that constrains the Nature
   * opération select options. The cdops are fetched internally (not a prop),
   * so a rule author limits the visible options by `setProp`-ing this
   * allow-list (HOC-injected) — mirroring `EstimationCard.deductionPostes`.
   * `undefined` or an empty array ⇒ NO constraint (every fetched cdop is
   * shown — byte-identical baseline render). Only the option list is filtered;
   * the field's selection state and the submit path are unaffected (the
   * `nature` value still flows through verbatim).
   */
  allowedNatures?: string[];
  onSettlementCreated?: () => void;
  /**
   * Per-tenant layout overrides injected by the `withComponentRules` HOC.
   * Underscored to match the injected meta-prop convention; `undefined` at
   * baseline ⇒ both the EntityForm config AND the provision-grid column config
   * are returned unchanged (byte-identical render). Rule authors target the
   * EntityForm field keys (`modeReglement` / `intervenant` / `libelle` /
   * `noPiece` / `nature` / `banque` / `monnaie`) and the stable provision-grid
   * column keys (`select` / `garantie` / `posteDeCout` / `montant`) to rename /
   * hide / reorder / tooltip without code. Hiding the `select` or `montant`
   * column is an explicit admin choice — selection + amount state stays
   * card-owned, so the submit path is never affected.
   */
  _layoutOverrides?: LayoutOverrides;
}

interface TableRow {
  row: ProvisionToSettleRow;
  selected: boolean;
  amount: string;
  error?: string;
}

const EUR_FORMATTER = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

function formatDdMmYyyy(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// ---------------------------------------------------------------------------
// Provision grid — declarative DataList config
//
// The provision grid is migrated from the base `<Table>` to the integrated
// `DataList` so its columns are reachable by tenant layout-override rules via
// stable keys (`select` / `garantie` / `posteDeCout` / `montant`). The grid's
// interactive bits — the selection Checkbox and the Montant input — must stay
// controlled by the card's live `rows` state, so they are rendered by
// module-singleton custom cell renderers that read a per-render `__ctx` carried
// on each row object (the same pattern as `MissingPiecesCard`). The rows are
// rebuilt every render so the closures stay fresh; no module-global mutable
// state, multi-instance safe.
// ---------------------------------------------------------------------------

interface ProvisionRowContext {
  /** Whether this provision is selected (contributes to total + postdom). */
  selected: boolean;
  /** Current amount string (pre-filled from PROV_RESTANTE). */
  amount: string;
  /** Clamp-validation error for this row's amount, if any. */
  error?: string;
  /** Display label of the guarantee (for the input/checkbox aria-labels). */
  libGar: string;
  /** Toggle this row's selection (client-side only — no API call). */
  onToggle: () => void;
  /** Update this row's amount (client-side only — no API call). */
  onAmountChange: (value: string) => void;
}

interface ProvisionGridRow extends Record<string, unknown> {
  garantie: string;
  posteDeCout: string;
  __ctx: ProvisionRowContext;
}

function getProvisionRowContext(
  row: Record<string, unknown>,
): ProvisionRowContext | undefined {
  return (row as Partial<ProvisionGridRow>).__ctx ?? undefined;
}

const SELECT_CELL_RENDERER = 'claimflow-settlementProvisionSelect';
const AMOUNT_CELL_RENDERER = 'claimflow-settlementProvisionAmount';

dataListWorkflowRegistry.registerCellRenderer(SELECT_CELL_RENDERER, (_value, row) => {
  const ctx = getProvisionRowContext(row);
  if (!ctx) return null;
  return (
    <Checkbox
      checked={ctx.selected}
      onChange={() => {
        ctx.onToggle();
      }}
      aria-label={`Sélectionner ${ctx.libGar}`}
    />
  );
});

dataListWorkflowRegistry.registerCellRenderer(AMOUNT_CELL_RENDERER, (_value, row) => {
  const ctx = getProvisionRowContext(row);
  if (!ctx) return null;
  return (
    <TextField
      type="number"
      size="sm"
      value={ctx.amount}
      onChange={(e) => {
        ctx.onAmountChange(e.target.value);
      }}
      error={Boolean(ctx.error)}
      errorMessage={ctx.error}
      aria-label={`Montant pour ${ctx.libGar}`}
    />
  );
});

/**
 * Builds the declarative provision-grid `DataListConfig` for an already-built
 * `ProvisionGridRow[]` (inline, client-only — DataList does not re-fetch). The
 * `select` / `montant` columns render the card-owned interactive controls via
 * the custom renderers above; `garantie` / `posteDeCout` are plain-text columns
 * reading `row.garantie` / `row.posteDeCout` (LIB_GAR / LIB_PSD). French labels
 * live only in `column.label` (display-only / overridable). Selection +
 * amount state stays card-side; this config is presentation only.
 */
function createProvisionGridConfig(rows: ProvisionGridRow[]): DataListConfig {
  return {
    id: 'claimflow-settlement-provisions',

    dataSource: {
      // Inline, client-only data — DataList does not re-fetch.
      data: rows,
    },

    columns: {
      select: {
        label: '',
        order: 1,
        align: 'left',
        width: '48px',
        type: 'custom',
        typeOptions: { renderer: SELECT_CELL_RENDERER },
        sortable: false,
      },
      garantie: {
        label: 'Garantie',
        order: 2,
        align: 'left',
        type: 'text',
        sortable: false,
      },
      posteDeCout: {
        label: 'Poste de coût',
        order: 3,
        align: 'left',
        type: 'text',
        sortable: false,
      },
      montant: {
        label: 'Montant',
        order: 4,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: AMOUNT_CELL_RENDERER },
        sortable: false,
      },
    },

    display: {
      size: 'default',
      hoverable: false,
    },

    sorting: {
      enabled: false,
    },

    toolbar: {
      visible: false,
    },

    // Fixed, finite provision grid — no pagination footer.
    pagination: {
      mode: 'scroll',
      pageSize: 999_999,
    },

    i18n: {
      emptyMessage: 'Aucune provision à régler',
    },
  };
}

function clampError(amount: string, max: string | number): string | undefined {
  const value = Number.parseFloat(amount);
  const maxValue = Number.parseFloat(String(max));
  if (Number.isNaN(value)) return 'Montant invalide';
  if (Number.isNaN(maxValue)) return undefined;

  if (maxValue < 0) {
    if (value > 0) return 'Le montant doit être négatif ou zéro';
    return undefined;
  }

  if (value < 0) return 'Le montant doit être positif';
  if (value > maxValue) return `Maximum : ${EUR_FORMATTER.format(maxValue)}`;
  return undefined;
}

function extractErpErrorMessage(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const response = (err as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const envelope = data as {
    error?: { message?: unknown };
    raw_response?: { message?: unknown };
  };
  if (typeof envelope.error?.message === 'string' && envelope.error.message.length > 0) {
    return envelope.error.message;
  }
  if (
    typeof envelope.raw_response?.message === 'string' &&
    envelope.raw_response.message.length > 0
  ) {
    return envelope.raw_response.message;
  }
  return undefined;
}

export function SettlementCard({
  ses,
  idSin,
  defaultModeReglement,
  defaultBanque,
  defaultMonnaie,
  defaultNature,
  allowedNatures,
  onSettlementCreated,
  _layoutOverrides,
}: Readonly<SettlementCardProps>) {
  const { toast } = useToast();

  const { config: remoteConfig } = useRemoteComponentConfig<SettlementCardConfig>({
    componentId: 'settlement-card',
  });

  // Three-tier resolution (explicit prop -> remote tenant config -> literal fallback).
  // Card renders with literals on first paint and silently swaps to tenant values
  // once the query resolves; this is intentional — do not "fix" by adding a Spinner.
  const {
    resolvedModeReglement,
    resolvedBanque,
    resolvedMonnaie,
    resolvedNature,
  } = useMemo(
    () => ({
      resolvedModeReglement: defaultModeReglement ?? remoteConfig?.defaultModeReglement ?? 'VIR',
      resolvedBanque: defaultBanque ?? remoteConfig?.defaultBanque ?? '21965',
      resolvedMonnaie: defaultMonnaie ?? remoteConfig?.defaultMonnaie ?? 'EU',
      resolvedNature: defaultNature ?? remoteConfig?.defaultNature ?? 'RGSINO',
    }),
    [defaultModeReglement, defaultBanque, defaultMonnaie, defaultNature, remoteConfig],
  );

  // Collapsed on first paint — every card in the claim view's right column
  // opens folded.
  const [expanded, setExpanded] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const [intervenants, setIntervenants] = useState<SinperRow[]>([]);
  const [banques, setBanques] = useState<RibRow[]>([]);
  const [modes, setModes] = useState<ModregRow[]>([]);
  const [monnaies, setMonnaies] = useState<MonnaieRow[]>([]);
  const [paramReglement, setParamReglement] = useState<ParamReglementRow | null>(null);
  const [cdops, setCdops] = useState<CdopRow[]>([]);

  const [rows, setRows] = useState<TableRow[]>([]);

  const formRef = useRef<UseEntityFormReturn | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchProvisions() {
      try {
        const response = await claimflowApiClient.get<ErpListResponse<ProvisionToSettleRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-provision-a-regler',
          { params: { SES: ses, id_sin: idSin, perPage: 9999 } },
        );
        if (!cancelled && response.data.success) {
          const data = response.data.data.data;
          setRows(
            data.map((row) => ({
              row,
              selected: false,
              amount: String(row.PROV_RESTANTE ?? '0'),
            })),
          );
        }
      } catch {
        // silently fail
      }
    }

    async function fetchIntervenants() {
      try {
        const response = await claimflowApiClient.get<ErpListResponse<SinperRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-sinper',
          { params: { SES: ses, id_sin: idSin, perPage: 9999 } },
        );
        if (!cancelled && response.data.success) {
          setIntervenants(response.data.data.data);
        }
      } catch {
        // silently fail
      }
    }

    async function fetchBanques() {
      try {
        const response = await claimflowApiClient.get<ErpListResponse<RibRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-ribper',
          { params: { SES: ses, perPage: 9999 } },
        );
        if (!cancelled && response.data.success) {
          setBanques(response.data.data.data);
        }
      } catch {
        // silently fail
      }
    }

    async function fetchModes() {
      try {
        const response = await claimflowApiClient.get<ErpListResponse<ModregRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-modreg',
          { params: { SES: ses, type: 'REGLEMENT', perPage: 9999 } },
        );
        if (!cancelled && response.data.success) {
          setModes(response.data.data.data);
        }
      } catch {
        // silently fail
      }
    }

    async function fetchMonnaies() {
      try {
        const response = await claimflowApiClient.get<ErpListResponse<MonnaieRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-monnaie',
          { params: { SES: ses, perPage: 9999 } },
        );
        if (!cancelled && response.data.success) {
          setMonnaies(response.data.data.data);
        }
      } catch {
        // silently fail
      }
    }

    async function fetchParamReglement() {
      try {
        const response = await claimflowApiClient.get<ErpListResponse<ParamReglementRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-param-reglement',
          { params: { SES: ses, id_sin: idSin } },
        );
        if (!cancelled && response.data.success) {
          const first = response.data.data.data[0];
          setParamReglement(first ?? null);
        }
      } catch {
        // silently fail
      }
    }

    async function fetchCdops() {
      try {
        const response = await claimflowApiClient.get<ErpListResponse<CdopRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-cdop',
          { params: { SES: ses, perPage: 9999 } },
        );
        if (!cancelled && response.data.success) {
          setCdops(response.data.data.data);
        }
      } catch {
        // silently fail
      }
    }

    fetchProvisions();
    fetchIntervenants();
    fetchBanques();
    fetchModes();
    fetchMonnaies();
    fetchParamReglement();
    fetchCdops();

    return () => {
      cancelled = true;
    };
  }, [ses, idSin]);

  const intervenantsById = useMemo(() => {
    const map = new Map<string, SinperRow>();
    intervenants.forEach((i) => map.set(String(i.ID_SINP), i));
    return map;
  }, [intervenants]);

  const modeItems = useMemo(
    () => modes.map((m) => ({ value: m.CD_MDR, label: m.LIB_MDR })),
    [modes],
  );

  const intervenantItems = useMemo(
    () => intervenants.map((i) => ({ value: String(i.ID_SINP), label: i.LIB })),
    [intervenants],
  );

  const banqueItems = useMemo(
    () => banques.map((b) => ({ value: String(b.ID_RIB), label: b.BANQ_RIB })),
    [banques],
  );

  const monnaieItems = useMemo(
    () => monnaies.map((m) => ({ value: m.CD_MON, label: m.LIB_MON })),
    [monnaies],
  );

  const cdopItems = useMemo(() => {
    // When the tenant supplies a non-empty `allowedNatures` allow-list, keep
    // only cdops whose `CD_OP` is in it before mapping to select items. An
    // `undefined`/empty list short-circuits to the full set (baseline parity).
    const allowSet =
      allowedNatures && allowedNatures.length > 0 ? new Set(allowedNatures) : null;
    const source = allowSet ? cdops.filter((c) => allowSet.has(c.CD_OP)) : cdops;
    return source.map((c) => ({ value: c.CD_OP, label: c.LIB_OP }));
  }, [cdops, allowedNatures]);

  const total = useMemo(
    () =>
      rows
        .filter((r) => r.selected)
        .reduce((acc, r) => acc + (Number.parseFloat(r.amount) || 0), 0),
    [rows],
  );

  const totalStr = useMemo(() => total.toFixed(2), [total]);

  const toggleRow = useCallback((index: number) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, selected: !r.selected } : r)),
    );
  }, []);

  const updateAmount = useCallback((index: number, value: string) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const error = clampError(value, r.row.PROV_RESTANTE);
        return { ...r, amount: value, error };
      }),
    );
  }, []);

  const mutationFn = useCallback(
    async (payload: Record<string, unknown> | FormData) => {
      const data =
        payload instanceof FormData
          ? Object.fromEntries(payload.entries())
          : payload;

      const today = formatDdMmYyyy(new Date());
      const selectedRows = rows.filter((r) => r.selected);

      const intervenantId = String(data.intervenant ?? '');
      const intervenant = intervenantsById.get(intervenantId);

      const body = {
        modeReg: String(data.modeReglement ?? '') || resolvedModeReglement,
        montantReg: totalStr,
        libReg: String(data.libelle ?? ''),
        banqueReg: String(data.banque ?? '') || resolvedBanque,
        noPieceReg: String(data.noPiece ?? ''),
        tireReg: '-',
        banqueEmettriceReg: '-',
        dtRemiseReg: today,
        deviseReg: String(data.monnaie ?? '') || resolvedMonnaie,
        categorie: intervenant?.CD_CATEG ?? '',
        idPer: intervenantId,
        numSoc: paramReglement?.NUM_SOC ?? '',
        nature: String(data.nature ?? '') || resolvedNature,
        dtOperation: today,
        idElem: idSin,
        cie: paramReglement?.CD_CIE ?? '',
        postdom: selectedRows.map((r) => ({
          cdGar: r.row.CD_GAR,
          mnt: r.amount,
          postDom: r.row.CD_PSD,
        })),
      };

      try {
        const response = await claimflowApiClient.post<ErpProxyResponse<unknown>>(
          '/claimflow/erp/registries/veos/endpoints/encaissement-trt',
          { data: body },
        );

        if (!response.data.success) {
          throw new Error(response.data.error?.message ?? 'Échec du règlement');
        }

        return response.data;
      } catch (err) {
        const message = extractErpErrorMessage(err);
        if (message) throw new Error(message);
        throw err;
      }
    },
    [
      rows,
      totalStr,
      intervenantsById,
      paramReglement,
      idSin,
      resolvedModeReglement,
      resolvedBanque,
      resolvedMonnaie,
      resolvedNature,
    ],
  );

  const baseFormConfig: EntityFormConfig = useMemo(
    () => ({
      id: 'settlement-card',
      endpoint: {
        erpEndpoint: '/claimflow/erp/registries/veos/endpoints/encaissement-trt',
        method: 'POST',
      },
      fields: {
        modeReglement: {
          type: 'radio',
          label: 'Mode de règlement',
          order: 1,
          colSpan: 2,
          validation: { required: 'Veuillez choisir un mode de règlement' },
          options: {
            items: modeItems,
            orientation: 'horizontal',
          } as RadioFieldOptions,
        },
        intervenant: {
          type: 'select',
          label: 'Intervenant',
          order: 2,
          colSpan: 2,
          validation: { required: 'Veuillez choisir un intervenant' },
          options: {
            items: intervenantItems,
            searchable: true,
            placeholder: 'Sélectionner un intervenant',
          } as SelectFieldOptions,
        },
        libelle: {
          type: 'text',
          label: 'Libellé',
          order: 3,
          colSpan: 2,
          validation: {
            required: 'Veuillez saisir un libellé',
            max: { value: 60, message: '60 caractères maximum' },
          },
          options: { maxLength: 60 } as TextFieldOptions,
        },
        noPiece: {
          type: 'text',
          label: 'N° de pièce',
          order: 4,
          colSpan: 2,
          validation: {
            required: 'Veuillez saisir un numéro de pièce',
            max: { value: 60, message: '60 caractères maximum' },
          },
          options: { maxLength: 60 } as TextFieldOptions,
        },
        nature: {
          type: 'select',
          label: 'Nature opération',
          order: 5,
          colSpan: 2,
          visible: showMore,
          options: {
            items: cdopItems,
            searchable: true,
            placeholder: "Sélectionner une nature d'opération",
          } as SelectFieldOptions,
        },
        banque: {
          type: 'select',
          label: 'Banque',
          order: 6,
          colSpan: 2,
          visible: showMore,
          options: {
            items: banqueItems,
            searchable: true,
            placeholder: 'Sélectionner une banque',
          } as SelectFieldOptions,
        },
        monnaie: {
          type: 'select',
          label: 'Monnaie',
          order: 7,
          colSpan: 2,
          visible: showMore,
          options: {
            items: monnaieItems,
            searchable: true,
            placeholder: 'Sélectionner une monnaie',
          } as SelectFieldOptions,
        },
      },
      actions: {
        submit: { visible: true, label: 'Valider le règlement' },
        cancel: { visible: false },
        reset: { visible: false },
      },
      layout: { columns: 2, gap: 'sm' },
    }),
    [modeItems, intervenantItems, cdopItems, banqueItems, monnaieItems, showMore],
  );

  const formConfig: EntityFormConfig = useMemo(
    () => applyLayoutOverrides(baseFormConfig, _layoutOverrides),
    [baseFormConfig, _layoutOverrides],
  );

  // Build the per-render provision-grid rows (data + live selection/amount
  // context) and the merged grid config. Rows are rebuilt whenever `rows`
  // changes so the renderers' closures stay fresh; `applyLayoutOverrides`
  // returns the column config unchanged at baseline (no rules) ⇒ identical
  // render. Selection + amount state lives in `rows`, owned by the card.
  const gridRows = useMemo<ProvisionGridRow[]>(
    () =>
      rows.map((r, idx) => ({
        garantie: r.row.LIB_GAR,
        posteDeCout: r.row.LIB_PSD,
        __ctx: {
          selected: r.selected,
          amount: r.amount,
          error: r.error,
          libGar: r.row.LIB_GAR,
          onToggle: () => {
            toggleRow(idx);
          },
          onAmountChange: (value: string) => {
            updateAmount(idx, value);
          },
        },
      })),
    [rows, toggleRow, updateAmount],
  );

  const baseGridConfig = useMemo(
    () => createProvisionGridConfig(gridRows),
    [gridRows],
  );

  usePublishLayoutConfig('claimflow.settlementCard', {
    ...buildPublishedConfigFromEntityForm(baseFormConfig, 'Règlement'),
    columns: buildPublishedConfigFromDataList(baseGridConfig).columns,
  });

  const gridConfig = useMemo(
    () => applyLayoutOverrides(baseGridConfig, _layoutOverrides),
    [baseGridConfig, _layoutOverrides],
  );

  const gridList = useDataList(gridConfig);

  const initialValues = useMemo(
    () => ({
      modeReglement: resolvedModeReglement,
      intervenant: '',
      libelle: '',
      noPiece: '',
      nature: resolvedNature,
      banque: resolvedBanque,
      monnaie: resolvedMonnaie,
    }),
    [resolvedModeReglement, resolvedNature, resolvedBanque, resolvedMonnaie],
  );

  const handleSuccess = useCallback(() => {
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        selected: false,
        amount: String(r.row.PROV_RESTANTE ?? '0'),
        error: undefined,
      })),
    );
    formRef.current?.reset(initialValues);
    toast({ variant: 'success', title: 'Règlement enregistré' });
    // The règlement is a new accounting movement of the claim — the
    // « Comptabilité » card re-fetches its movements (22853 follow-up).
    useClaimDataStore.getState().bumpComptabilite();
    onSettlementCreated?.();
    setExpanded(false);
  }, [initialValues, toast, onSettlementCreated]);

  const form = useEntityForm({
    config: formConfig,
    initialValues,
    onSuccess: handleSuccess,
    mutationFn,
  });

  formRef.current = form;

  const selectedCount = rows.filter((r) => r.selected).length;
  const hasRowError = rows.some((r) => r.selected && r.error);
  const requiredEmpty =
    !form.getValue('modeReglement') ||
    !form.getValue('intervenant') ||
    !String(form.getValue('libelle') ?? '').trim() ||
    !String(form.getValue('noPiece') ?? '').trim();

  const submitDisabled =
    form.isSubmitting ||
    selectedCount === 0 ||
    total < 0 ||
    hasRowError ||
    requiredEmpty;

  return (
    <Card id={actionAnchorId('claimflow.settlementCard')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        <Icon icon={Wallet} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Règlement', _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        <Text size="sm" color="muted" className="mb-4">
          Sélectionnez les provisions à régler :
        </Text>

        <DataList config={gridConfig} list={gridList}>
          <DataList.Table />
        </DataList>

        <div className="mt-3 flex justify-end">
          <Text size="sm" weight="bold">
            Total sélectionné : {EUR_FORMATTER.format(total)}
          </Text>
        </div>

        <div className="mt-4">
          <EntityForm config={formConfig} form={form}>
            <EntityForm.Alert />
            <EntityForm.Fields />
            <div className="pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMore((s) => !s)}
                type="button"
              >
                {showMore
                  ? <>… Moins d&apos;options</>
                  : <>… Plus d&apos;options (nature opération, banque, monnaie)</>}
              </Button>
            </div>
            {formConfig.actions?.submit?.visible !== false && (
              <div className="pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  leftIcon={<Icon icon={Save} size="sm" />}
                  loading={form.isSubmitting}
                  disabled={submitDisabled}
                  className="w-full"
                >
                  {formConfig.actions?.submit?.label ?? 'Valider le règlement'}
                </Button>
              </div>
            )}
          </EntityForm>
        </div>
      </Card.Content>
    </Card>
  );
}
